import React, { useState, useEffect } from 'react';

const API = 'https://green-energy.com.co/api';

const getStatusConfig = (status, isLive) => {
  if (!isLive) return { label:'Sin conexión', color:'#555', bg:'#1e1e1e', border:'#2a2a2a' };
  return {
    Available: { label:'Disponible', color:'#4ade80', bg:'#1a3a2a', border:'#1d6a3a' },
    Charging:  { label:'Cargando',   color:'#60a5fa', bg:'#1a2a3a', border:'#1a4a7a' },
    Faulted:   { label:'Falla',      color:'#f87171', bg:'#3a1a1a', border:'#7a1a1a' },
    Offline:   { label:'Sin red',    color:'#888',    bg:'#2a2a2a', border:'#3a3a3a' },
  }[status] || { label:'Sin conexión', color:'#555', bg:'#1e1e1e', border:'#2a2a2a' };
};

const socColor = s => s>=75?'#4ade80':s>=40?'#60a5fa':'#f59e0b';

export default function Monitor() {
  const [chargers, setChargers] = useState([]);
  const [liveChargers, setLiveChargers] = useState([]);
  const [filterCop, setFilterCop] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [vista, setVista] = useState('pistolas');
  const [search, setSearch] = useState('');
  const [cmdResult, setCmdResult] = useState(null);
  const [sending, setSending] = useState('');

  const fetchData = () => {
    fetch(`${API}/chargers`).then(r=>r.json()).then(setChargers).catch(()=>{});
    fetch(`${API}/live`).then(r=>r.json()).then(setLiveChargers).catch(()=>{});
  };

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 5000);
    return () => clearInterval(iv);
  }, []);

  const enviarComando = async (cpId, accion, connectorId) => {
    setSending(`${cpId}_${accion}`);
    setCmdResult(null);
    try {
      const resp = await fetch(`${API}/comando`, {
        method: 'POST',
        headers: { 'Content-Type':'application/json' },
        body: JSON.stringify({ cpId, accion, connectorId })
      });
      const data = await resp.json();
      setCmdResult({ ok:data.ok, msg: data.ok ? `✅ Comando ${accion} enviado a ${cpId}` : `❌ ${data.error}` });
    } catch(e) {
      setCmdResult({ ok:false, msg:'❌ Error de conexión' });
    }
    setSending('');
    setTimeout(() => setCmdResult(null), 5000);
  };

  const isLive = cpId => liveChargers.includes(cpId);

  const pistolas = chargers
    .filter(c => !filterCop || c.cop===filterCop)
    .flatMap(c => {
      const live = isLive(c.id);
      const conns = [
        { id:1, status: live ? (c.status||'Available') : 'Offline', soc:c.soc, power:c.power, zonal:c.zonal },
        { id:2, status: live ? 'Available' : 'Offline', soc:null, power:0, zonal:null },
      ];
      return conns.map(conn => ({
        cargadorId: c.id, cop: c.cop, connectorId: conn.id,
        status: conn.status, soc: conn.soc, power: conn.power??0,
        zonal: conn.zonal, lastSeen: c.last_seen||c.lastSeen, live
      }));
    })
    .filter(p => {
      if (filterStatus === 'live' && !p.live) return false;
      if (filterStatus === 'Available' && (p.status!=='Available'||!p.live)) return false;
      if (filterStatus === 'Charging' && p.status!=='Charging') return false;
      if (filterStatus === 'Offline' && p.live) return false;
      if (search && !p.cargadorId.toLowerCase().includes(search.toLowerCase()) && !p.zonal?.includes(search)) return false;
      return true;
    });

  const totalPistolas = pistolas.length;
  const enVivo = chargers.filter(c=>isLive(c.id)).length;
  const disponibles = pistolas.filter(p=>p.status==='Available'&&p.live).length;
  const cargando = pistolas.filter(p=>p.status==='Charging').length;
  const sinConexion = chargers.filter(c=>!isLive(c.id)).length;
  const potenciaTotal = pistolas.reduce((a,p)=>a+(p.power||0),0).toFixed(1);

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1.5rem',flexWrap:'wrap',gap:'12px'}}>
        <div className="page-title" style={{margin:0}}>Monitor en vivo</div>
        <div style={{display:'flex',gap:'6px'}}>
          <button onClick={()=>setVista('pistolas')}
            style={{padding:'6px 14px',border:'1px solid',borderRadius:'6px',cursor:'pointer',fontSize:'13px',
              background:vista==='pistolas'?'#2a2d3a':'#1a1d27',borderColor:vista==='pistolas'?'#4ade80':'#2a2d3a',color:vista==='pistolas'?'#4ade80':'#666'}}>
            🔌 Por pistola
          </button>
          <button onClick={()=>setVista('cargadores')}
            style={{padding:'6px 14px',border:'1px solid',borderRadius:'6px',cursor:'pointer',fontSize:'13px',
              background:vista==='cargadores'?'#2a2d3a':'#1a1d27',borderColor:vista==='cargadores'?'#4ade80':'#2a2d3a',color:vista==='cargadores'?'#4ade80':'#666'}}>
            ⚡ Por cargador
          </button>
        </div>
      </div>

      {cmdResult && (
        <div style={{background:cmdResult.ok?'#1a3a2a':'#3a1a1a',border:`1px solid ${cmdResult.ok?'#4ade80':'#f87171'}`,borderRadius:'8px',padding:'10px 16px',marginBottom:'1.25rem',fontSize:'13px',color:cmdResult.ok?'#4ade80':'#f87171'}}>
          {cmdResult.msg}
        </div>
      )}

      <div className="metrics">
        <div className="metric-card">
          <div className="metric-label">En vivo</div>
          <div className="metric-value" style={{color:'#4ade80'}}>{enVivo}</div>
          <div className="metric-sub">conectados a tu servidor</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Disponibles</div>
          <div className="metric-value" style={{color:'#4ade80'}}>{disponibles}</div>
          <div className="metric-sub">listos para cargar</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Cargando ahora</div>
          <div className="metric-value" style={{color:'#60a5fa'}}>{cargando}</div>
          <div className="metric-sub">{potenciaTotal} kW total</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Sin conexión</div>
          <div className="metric-value" style={{color:'#555'}}>{sinConexion}</div>
          <div className="metric-sub">apuntando a Enerlink</div>
        </div>
      </div>

      <div className="filters">
        <input placeholder="Buscar cargador o zonal..." value={search} onChange={e=>setSearch(e.target.value)} style={{width:'200px'}} />
        <select value={filterCop} onChange={e=>setFilterCop(e.target.value)}>
          <option value="">Todos los COP</option>
          <option value="Fontibón">Fontibón</option>
          <option value="Usme">Usme</option>
        </select>
        <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
          <option value="">Todos</option>
          <option value="live">En vivo</option>
          <option value="Available">Disponibles</option>
          <option value="Charging">Cargando</option>
          <option value="Offline">Sin conexión</option>
        </select>
        <span style={{fontSize:'12px',color:'#555',marginLeft:'auto'}}>{pistolas.length} pistolas · actualiza cada 5s</span>
      </div>

      {vista==='pistolas' ? (
        <div className="card">
          <table>
            <thead>
              <tr>
                <th>Cargador</th><th>P.</th><th>COP</th><th>Estado</th>
                <th>Bus / Zonal</th><th>SoC</th><th>Potencia</th>
                <th>Conexión</th><th>Comandos</th>
              </tr>
            </thead>
            <tbody>
              {pistolas.map((p,i) => {
                const cfg = getStatusConfig(p.status, p.live);
                return (
                  <tr key={i} style={{opacity: p.live ? 1 : 0.45}}>
                    <td><span className="badge">{p.cargadorId}</span></td>
                    <td style={{color:'#666',fontSize:'11px'}}>P{p.connectorId}</td>
                    <td style={{color:'#aaa',fontSize:'12px'}}>{p.cop}</td>
                    <td>
                      <span style={{background:cfg.bg,color:cfg.color,border:`1px solid ${cfg.border}`,padding:'2px 8px',borderRadius:'5px',fontSize:'11px',fontWeight:'500'}}>
                        {cfg.label}
                      </span>
                    </td>
                    <td>{p.zonal?<span style={{color:'#4ade80',fontSize:'12px',fontWeight:'500'}}>{p.zonal}</span>:<span style={{color:'#333'}}>—</span>}</td>
                    <td>
                      {p.soc!==null&&p.soc!==undefined&&p.live?(
                        <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
                          <div className="soc-bar-bg" style={{width:'60px'}}><div className="soc-bar-fill" style={{width:`${p.soc}%`,background:socColor(p.soc)}}></div></div>
                          <span style={{fontSize:'11px',fontWeight:'500',color:socColor(p.soc)}}>{p.soc}%</span>
                        </div>
                      ):<span style={{color:'#333'}}>—</span>}
                    </td>
                    <td>{p.status==='Charging'&&p.power>0?<span style={{color:'#60a5fa',fontWeight:'500',fontSize:'12px'}}>{p.power} kW</span>:<span style={{color:'#333'}}>—</span>}</td>
                    <td>
                      <span style={{fontSize:'11px',fontWeight:'500',color:p.live?'#4ade80':'#444'}}>
                        {p.live?'🟢 En vivo':'⚫ Sin conexión'}
                      </span>
                    </td>
                    <td>
                      {p.live ? (
                        <div style={{display:'flex',gap:'4px'}}>
                          <button onClick={()=>enviarComando(p.cargadorId,'RemoteStartTransaction',p.connectorId)} disabled={!!sending}
                            style={{padding:'3px 7px',background:'#1a3a2a',border:'1px solid #1d6a3a',borderRadius:'5px',color:'#4ade80',cursor:'pointer',fontSize:'10px'}}>▶ Start</button>
                          <button onClick={()=>enviarComando(p.cargadorId,'RemoteStopTransaction',p.connectorId)} disabled={!!sending}
                            style={{padding:'3px 7px',background:'#3a1a1a',border:'1px solid #7a1a1a',borderRadius:'5px',color:'#f87171',cursor:'pointer',fontSize:'10px'}}>⏹ Stop</button>
                          <button onClick={()=>enviarComando(p.cargadorId,'Reset',0)} disabled={!!sending}
                            style={{padding:'3px 7px',background:'#2a2d3a',border:'1px solid #3a3d4a',borderRadius:'5px',color:'#aaa',cursor:'pointer',fontSize:'10px'}}>🔄</button>
                        </div>
                      ) : <span style={{color:'#333',fontSize:'11px'}}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:'12px'}}>
          {chargers.filter(c=>!filterCop||c.cop===filterCop).map(c => {
            const live = isLive(c.id);
            const cfg = getStatusConfig(c.status, live);
            return (
              <div key={c.id} style={{background:'#1a1d27',border:`1px solid ${cfg.border}`,borderRadius:'10px',padding:'1rem',opacity:live?1:0.5}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'10px'}}>
                  <span className="badge">{c.id}</span>
                  <div style={{display:'flex',gap:'6px',alignItems:'center'}}>
                    <span style={{fontSize:'11px',color:live?'#4ade80':'#444'}}>{live?'🟢':'⚫'}</span>
                    <span style={{background:cfg.bg,color:cfg.color,padding:'2px 8px',borderRadius:'5px',fontSize:'11px',fontWeight:'500'}}>{cfg.label}</span>
                  </div>
                </div>
                <div style={{fontSize:'12px',color:'#666',marginBottom:'8px'}}>{c.cop}</div>
                {live && c.soc!==null&&c.soc!==undefined&&(
                  <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'10px'}}>
                    <div className="soc-bar-bg" style={{flex:1}}><div className="soc-bar-fill" style={{width:`${c.soc}%`,background:socColor(c.soc)}}></div></div>
                    <span style={{fontSize:'12px',fontWeight:'500',color:socColor(c.soc)}}>{c.soc}%</span>
                  </div>
                )}
                {live && (
                  <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
                    <button onClick={()=>enviarComando(c.id,'RemoteStartTransaction',1)}
                      style={{flex:1,padding:'5px',background:'#1a3a2a',border:'1px solid #1d6a3a',borderRadius:'6px',color:'#4ade80',cursor:'pointer',fontSize:'11px'}}>▶ P1</button>
                    <button onClick={()=>enviarComando(c.id,'RemoteStartTransaction',2)}
                      style={{flex:1,padding:'5px',background:'#1a3a2a',border:'1px solid #1d6a3a',borderRadius:'6px',color:'#4ade80',cursor:'pointer',fontSize:'11px'}}>▶ P2</button>
                    <button onClick={()=>enviarComando(c.id,'RemoteStopTransaction',0)}
                      style={{flex:1,padding:'5px',background:'#3a1a1a',border:'1px solid #7a1a1a',borderRadius:'6px',color:'#f87171',cursor:'pointer',fontSize:'11px'}}>⏹ Stop</button>
                    <button onClick={()=>enviarComando(c.id,'Reset',0)}
                      style={{padding:'5px 8px',background:'#2a2d3a',border:'1px solid #3a3d4a',borderRadius:'6px',color:'#aaa',cursor:'pointer',fontSize:'11px'}}>🔄</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
